import React, { useState, useEffect } from 'react';
import ReportTable, { Column } from '../ReportTable';
import { Tenant, Property } from '../../types';

interface TenantReportsProps {
  tenants: Tenant[];
  properties: Property[];
  initialReport?: string;
}

const TenantReports: React.FC<TenantReportsProps> = ({ tenants, properties, initialReport }) => {
  const [selectedReport, setSelectedReport] = useState<string>(initialReport || 'brief');
  const [endingFilter, setEndingFilter] = useState<number>(30); // days
  const [nationalityFilter, setNationalityFilter] = useState<string>('All');

  useEffect(() => {
    if (initialReport) {
      setSelectedReport(initialReport);
    }
  }, [initialReport]);

  // Helper to find unit
  const getTenantUnit = (tenantFirst: string, tenantLast: string) => {
      let occupied = '-';
      properties.forEach(p => {
         p.units.forEach(u => {
             if (u.tenantName && (u.tenantName.includes(tenantFirst) || u.tenantName.includes(tenantLast))) {
                 occupied = `${p.name} - ${u.unitNumber}`;
             }
         });
      });
      return occupied;
  };

  const filteredTenants = tenants.filter(t => {
      const nationalityMatch = nationalityFilter === 'All' || t.nationality === nationalityFilter;
      return nationalityMatch;
  });

  const briefData = filteredTenants.map(t => ({
      name: `${t.firstName} ${t.lastName}`,
      unit: getTenantUnit(t.firstName, t.lastName),
      start: t.contractStart ? new Date(t.contractStart).toLocaleDateString('ar-KW') : '-',
      end: t.contractEnd ? new Date(t.contractEnd).toLocaleDateString('ar-KW') : '-',
      mobile: t.mobile
  }));

  const briefColumns: Column[] = [
      { header: 'اسم المستأجر', accessor: 'name' },
      { header: 'الهاتف', accessor: 'mobile' },
      { header: 'العقار والوحدة', accessor: 'unit' },
      { header: 'تاريخ بداية العقد', accessor: 'start' },
      { header: 'تاريخ نهاية العقد', accessor: 'end' }
  ];

  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + endingFilter);

  const renewingData = filteredTenants.filter(t => {
      if (!t.contractEnd) return false;
      const end = new Date(t.contractEnd);
      return end >= today && end <= futureDate;
  }).map(t => {
      const daysLeft = Math.ceil((new Date(t.contractEnd!).getTime() - today.getTime()) / (1000 * 3600 * 24));
      return {
          name: `${t.firstName} ${t.lastName}`,
          mobile: t.mobile,
          end: new Date(t.contractEnd!).toLocaleDateString('ar-KW'),
          daysLeft
      };
  }).sort((a,b) => a.daysLeft - b.daysLeft);

  const renewingColumns: Column[] = [
      { header: 'اسم المستأجر', accessor: 'name' },
      { header: 'الهاتف', accessor: 'mobile' },
      { header: 'تاريخ النهاية', accessor: 'end' },
      { header: 'الأيام المتبقية', accessor: 'daysLeft', render: val => <span className={`font-bold ${Number(val) < 15 ? 'text-red-600' : 'text-orange-600'}`}>{val} يوم</span> }
  ];

  const detailedData = filteredTenants.map((t, idx) => ({
      ...t,
      status: t.contractEnd && new Date(t.contractEnd) < today ? 'عقد منتهي/سابق' : 'مستأجر حالي',
      name: `${t.firstName} ${t.lastName}`,
      unit: getTenantUnit(t.firstName, t.lastName)
  }));

  const detailedColumns: Column[] = [
      { header: 'الاسم', accessor: 'name' },
      { header: 'الرقم المدني', accessor: 'civilId' },
      { header: 'الجنسية', accessor: 'nationality' },
      { header: 'العقار والوحدة', accessor: 'unit' },
      { header: 'بداية العقد', accessor: 'contractStart', render: val => val ? new Date(val).toLocaleDateString('ar-KW') : '-' },
      { header: 'نهاية العقد', accessor: 'contractEnd', render: val => val ? new Date(val).toLocaleDateString('ar-KW') : '-' },
      { header: 'الحالة', accessor: 'status', render: val => <span className={val.includes('حالي') ? 'text-green-600 font-bold' : 'text-slate-400'}>{val}</span> }
  ];

  const customFilters = (
    <div className="flex items-center gap-3">
        <select 
          className="border-slate-200 rounded-xl p-2 bg-white text-xs font-bold shadow-sm"
          value={nationalityFilter} 
          onChange={(e) => setNationalityFilter(e.target.value)}
        >
          <option value="All">جميع الجنسيات</option>
          {Array.from(new Set(tenants.map(t => t.nationality))).filter(Boolean).map(n => (
              <option key={n} value={n}>{n}</option>
          ))}
        </select>

        {selectedReport === 'renewals' && (
            <select 
            className="border-slate-200 rounded-xl p-2 bg-white text-xs font-bold shadow-sm"
            value={endingFilter} 
            onChange={(e) => setEndingFilter(Number(e.target.value))}
            >
                <option value={30}>ينتهي خلال 30 يوم</option>
                <option value={60}>ينتهي خلال 60 يوم</option>
                <option value={90}>ينتهي خلال 90 يوم</option>
            </select>
        )}
    </div>
  );

  return (
    <div className="flex flex-col h-full space-y-6">
      <div className="px-6 py-4 bg-white rounded-3xl border border-slate-200 shadow-sm flex items-center justify-between">
         <div className="flex items-center gap-4">
            <span className="text-sm font-black text-slate-400">الفئة المختارة:</span>
            <div className="flex bg-slate-100 p-1 rounded-2xl">
               {(['brief', 'renewals', 'detailed'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setSelectedReport(tab)}
                    className={`px-6 py-2 rounded-xl text-xs font-black transition-all ${
                        selectedReport === tab ? 'bg-white text-fly-600 shadow-md' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {tab === 'brief' ? 'مختصر' : tab === 'renewals' ? 'تجديد العقود' : 'التاريخ الكامل'}
                  </button>
               ))}
            </div>
         </div>
      </div>

      <div className="flex-1">
          {selectedReport === 'brief' && <ReportTable title="تقرير قائمة المستأجرين" columns={briefColumns} data={briefData} customFilters={customFilters} />}
          {selectedReport === 'renewals' && <ReportTable title={`إشعارات انتهاء العقود (${endingFilter} يوم)`} columns={renewingColumns} data={renewingData} customFilters={customFilters} />}
          {selectedReport === 'detailed' && <ReportTable title="سجل المستأجرين التفصيلي" columns={detailedColumns} data={detailedData} customFilters={customFilters} />}
      </div>
    </div>
  );
};

export default TenantReports;
